/**
 * Formulario de «Combos y recetas» (spec 20): lo que se edita en pantalla (textos como los teclea la dueña, con coma
 * decimal) y su paso a la entrada del servidor (`comboInput`), la validación con los mensajes por campo y la definición
 * para «Probar» sin guardar (la misma función `calcularCombo` que usa el servidor).
 *
 * Cada componente lleva una `clave` estable: el derivado apunta a la clave de su base, así reordenar o quitar filas no
 * rompe a qué componente apunta (al guardar se convierte en el número de orden).
 */
import {
  calcularCombo,
  comboInput,
  ErrorCombo,
  formatearCantidad,
  Money,
  UNIDAD_MEDIDA_ETIQUETA,
  type ComboDTO,
  type ComboEntrada,
  type ComboInput,
  type DefinicionCombo,
  type ModoComponente,
  type PresentacionDTO,
  type ProductoResumenDTO,
  type RedondeoComponente,
  type RenglonComboDTO,
  type TipoCombo,
  type UnidadMedidaCombo,
} from '@mg/shared';
import { normalizarDecimal } from '@/lib/montos.ts';

export interface PresentacionElegible {
  id: string;
  nombre: string;
  unidades: string;
}

export interface ProductoElegido {
  producto_id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  /** Fracción mínima de venta del producto (para el redondeo «a la fracción mínima»). */
  fraccion_minima: string;
  presentaciones: PresentacionElegible[];
  /** Ya trae todas sus presentaciones. Las de un combo guardado traen solo la elegida hasta que se piden al servidor. */
  completo: boolean;
}

export interface FilaEmpaque {
  clave: string;
  producto: ProductoElegido;
  presentacion_id: string;
  contenido: string;
}

export interface FilaComponente {
  clave: string;
  producto: ProductoElegido;
  presentacion_id: string;
  modo: ModoComponente;
  factor: string;
  contenido: string;
  /** Derivado: la clave del componente del que sale. */
  base_clave: string | null;
  ajuste: string;
  redondeo: RedondeoComponente;
  /** Vacío = el descuento del combo. */
  descuento: string;
  empaques: FilaEmpaque[];
}

export interface FormularioCombo {
  codigo: string;
  nombre: string;
  descripcion: string;
  tipo: TipoCombo;
  unidad_medida: UnidadMedidaCombo | '';
  descuento: string;
  componentes: FilaComponente[];
}

let siguiente = 0;
export const nuevaClave = () => `k${++siguiente}`;

/** Texto como lo teclea la dueña («0,36», «-10», «1.000») a decimal canónico («0.36»); vacío queda vacío. */
export const aDecimal = (texto: string) => normalizarDecimal(texto.replace(/−/g, '-').replace(/%/g, ''));
/** Decimal del servidor («0.36000000») al texto de la pantalla («0,36»). */
export const aTexto = (v: string | null | undefined) => (v === null || v === undefined || v === '' ? '' : formatearCantidad(Money.parse(v), 8));

const esUno = (unidades: string) => {
  try {
    return Money.parse(unidades).eq('1');
  } catch {
    return false;
  }
};

/** Las presentaciones activas de un producto del catálogo, en su orden. */
function presentacionesActivas(ps: PresentacionDTO[]): PresentacionElegible[] {
  return ps
    .filter((p) => p.activa)
    .toSorted((a, b) => a.orden - b.orden)
    .map((p) => ({ id: p.id, nombre: p.nombre, unidades: p.unidades }));
}

export function productoElegido(p: ProductoResumenDTO): ProductoElegido {
  return { producto_id: p.id, codigo: p.codigo, nombre: p.nombre, activo: p.activo, fraccion_minima: p.fraccion_minima, presentaciones: presentacionesActivas(p.presentaciones), completo: true };
}

/** La presentación que se propone al elegir un producto: la de 1 unidad; si no tiene, la primera. */
export function presentacionPorOmision(p: ProductoElegido): string {
  return (p.presentaciones.find((x) => esUno(x.unidades)) ?? p.presentaciones[0])?.id ?? '';
}

export function formularioVacio(): FormularioCombo {
  return { codigo: '', nombre: '', descripcion: '', tipo: 'por_medida', unidad_medida: 'm2', descuento: '', componentes: [] };
}

export function filaNueva(p: ProductoElegido, tipo: TipoCombo): FilaComponente {
  return {
    clave: nuevaClave(),
    producto: p,
    presentacion_id: presentacionPorOmision(p),
    modo: tipo === 'fijo' ? 'fijo' : 'consumo',
    factor: '',
    contenido: '1',
    base_clave: null,
    ajuste: '',
    redondeo: 'entero',
    descuento: '',
    empaques: [],
  };
}

export function empaqueNuevo(p: ProductoElegido): FilaEmpaque {
  return { clave: nuevaClave(), producto: p, presentacion_id: presentacionPorOmision(p), contenido: '' };
}

/** Un combo guardado, listo para editar. */
export function formularioDesdeCombo(c: ComboDTO): FormularioCombo {
  const claves = new Map(c.componentes.map((k) => [k.orden, nuevaClave()]));
  return {
    codigo: c.codigo,
    nombre: c.nombre,
    descripcion: c.descripcion,
    tipo: c.tipo,
    unidad_medida: c.unidad_medida ?? '',
    descuento: Money.parse(c.descuento_pct).esPositivo() ? aTexto(c.descuento_pct) : '',
    componentes: c.componentes.map((k) => ({
      clave: claves.get(k.orden)!,
      producto: {
        producto_id: k.producto_id,
        codigo: k.producto_codigo,
        nombre: k.producto_nombre,
        activo: k.producto_activo,
        fraccion_minima: k.fraccion_minima,
        presentaciones: [{ id: k.presentacion_id, nombre: k.presentacion_nombre, unidades: k.unidades_presentacion }],
        completo: false,
      },
      presentacion_id: k.presentacion_id,
      modo: k.modo,
      factor: aTexto(k.factor),
      contenido: aTexto(k.contenido),
      base_clave: k.base_orden === null ? null : (claves.get(k.base_orden) ?? null),
      ajuste: aTexto(k.ajuste_pct),
      redondeo: k.redondeo,
      descuento: aTexto(k.descuento_pct),
      empaques: k.empaques.map((e) => ({
        clave: nuevaClave(),
        producto: {
          producto_id: e.producto_id,
          codigo: e.producto_codigo,
          nombre: e.producto_nombre,
          activo: true,
          fraccion_minima: '1',
          presentaciones: [{ id: e.presentacion_id, nombre: e.presentacion_nombre, unidades: '1' }],
          completo: false,
        },
        presentacion_id: e.presentacion_id,
        contenido: aTexto(e.contenido),
      })),
    })),
  };
}

/** Lo que va al servidor (y a `comboInput`): decimales canónicos, el derivado con el número de su base. */
export function aEntrada(f: FormularioCombo): ComboEntrada {
  const posicion = new Map(f.componentes.map((k, i) => [k.clave, i + 1]));
  return {
    codigo: f.codigo,
    nombre: f.nombre,
    descripcion: f.descripcion,
    tipo: f.tipo,
    unidad_medida: f.tipo === 'por_medida' ? f.unidad_medida || null : null,
    descuento_pct: aDecimal(f.descuento) || '0',
    componentes: f.componentes.map((k) => {
      const derivado = k.modo === 'derivado';
      return {
        producto_id: k.producto.producto_id,
        presentacion_id: k.presentacion_id,
        modo: k.modo,
        factor: derivado ? null : aDecimal(k.factor) || null,
        contenido: aDecimal(k.contenido) || '1',
        base_orden: derivado && k.base_clave ? (posicion.get(k.base_clave) ?? null) : null,
        // El ajuste vacío es 0 % (la secundaria igual a las láminas)
        ajuste_pct: derivado ? aDecimal(k.ajuste) || '0' : null,
        redondeo: k.redondeo,
        descuento_pct: aDecimal(k.descuento) || null,
        empaques: k.empaques.map((e) => ({ producto_id: e.producto.producto_id, presentacion_id: e.presentacion_id, contenido: aDecimal(e.contenido) })),
      };
    }),
  };
}

export type Validacion = { ok: true; datos: ComboInput } | { ok: false; errores: Record<string, string> };

/**
 * Validación con el schema compartido. Los errores van por ruta («codigo», «componentes.0.factor», «componentes.2»):
 * cada uno se muestra debajo de su campo; el primero de cada ruta gana.
 */
export function validarFormulario(f: FormularioCombo): Validacion {
  const r = comboInput.safeParse(aEntrada(f));
  if (r.success) return { ok: true, datos: r.data };
  const errores: Record<string, string> = {};
  for (const i of r.error.issues) {
    const ruta = i.path.map(String).join('.');
    if (!(ruta in errores)) errores[ruta] = mensajeClaro(f, ruta, i.message);
  }
  return { ok: false, errores };
}

/** Un campo vacío dice qué falta, no «Monto inválido». */
function mensajeClaro(f: FormularioCombo, ruta: string, mensaje: string): string {
  const empaque = /^componentes\.(\d+)\.empaques\.(\d+)\.contenido$/.exec(ruta);
  if (empaque && !f.componentes[Number(empaque[1])]?.empaques[Number(empaque[2])]?.contenido.trim()) return 'Escribe lo que trae este empaque (en la unidad del consumo)';
  if (ruta === 'nombre' && !f.nombre.trim()) return 'Escribe el nombre del combo';
  return mensaje;
}

/** Los errores de una fila que no tienen un campo propio en pantalla (producto repetido, empaques…). */
export function erroresSueltos(errores: Record<string, string>, prefijo: string, conCampo: string[]): string[] {
  const propios = new Set(conCampo.map((c) => `${prefijo}.${c}`));
  return Object.entries(errores)
    .filter(([ruta]) => (ruta === prefijo || ruta.startsWith(`${prefijo}.`)) && !propios.has(ruta))
    .map(([, m]) => m)
    .filter((m, i, todos) => todos.indexOf(m) === i);
}

/** La definición para calcular en pantalla lo que todavía no se guardó (con los datos del catálogo de cada producto). */
export function definicionLocal(f: FormularioCombo, datos: ComboInput): DefinicionCombo {
  return {
    tipo: datos.tipo,
    descuento_pct: datos.descuento_pct,
    componentes: datos.componentes.map((k, i) => {
      const fila = f.componentes[i]!;
      const pres = fila.producto.presentaciones.find((p) => p.id === k.presentacion_id);
      return {
        orden: i + 1,
        producto_id: k.producto_id,
        presentacion_id: k.presentacion_id,
        producto_codigo: fila.producto.codigo,
        producto_nombre: fila.producto.nombre,
        unidades_presentacion: pres?.unidades ?? '1',
        fraccion_minima: fila.producto.fraccion_minima,
        modo: k.modo,
        factor: k.factor,
        contenido: k.contenido,
        base_orden: k.base_orden,
        ajuste_pct: k.ajuste_pct,
        redondeo: k.redondeo,
        descuento_pct: k.descuento_pct,
        empaques: k.empaques.map((e, j) => ({
          producto_id: e.producto_id,
          presentacion_id: e.presentacion_id,
          producto_codigo: fila.empaques[j]!.producto.codigo,
          producto_nombre: fila.empaques[j]!.producto.nombre,
          contenido: e.contenido,
        })),
      };
    }),
  };
}

/** «Probar» con lo que está en pantalla: los renglones, o por qué todavía no se puede calcular. */
export function probarLocal(f: FormularioCombo, medidaTexto: string): { renglones: RenglonComboDTO[] } | { error: string } | null {
  const medida = aDecimal(medidaTexto);
  if (!medida) return null;
  if (!/^\d{1,12}(\.\d{1,8})?$/.test(medida)) return { error: f.tipo === 'fijo' ? 'Escribe el número de combos (1, 2, 3…)' : 'Escribe la medida como 26 o 12,5' };
  const v = validarFormulario(f);
  if (!v.ok) return { error: `Para probar, corrige primero lo marcado en el combo: ${Object.values(v.errores)[0]}` };
  try {
    return { renglones: calcularCombo(definicionLocal(f, v.datos), medida) };
  } catch (e) {
    if (e instanceof ErrorCombo) return { error: e.message };
    throw e;
  }
}

/** ¿Lo de la pantalla difiere de lo guardado? */
export const hayCambios = (f: FormularioCombo, inicial: FormularioCombo) => JSON.stringify(aEntrada(f)) !== JSON.stringify(aEntrada(inicial));

/** Cambiar el tipo: el combo fijo no lleva unidad ni modos por medida (pasan a «Cantidad fija»); la receta propone m². */
export function cambiarTipo(f: FormularioCombo, tipo: TipoCombo): FormularioCombo {
  return {
    ...f,
    tipo,
    unidad_medida: tipo === 'fijo' ? '' : f.unidad_medida || 'm2',
    componentes: tipo === 'fijo' ? f.componentes.map((k) => (k.modo === 'consumo' || k.modo === 'rendimiento' ? { ...k, modo: 'fijo' } : k)) : f.componentes,
  };
}

/** Sube (−1) o baja (+1) un componente. */
export function moverComponente(f: FormularioCombo, i: number, paso: -1 | 1): FormularioCombo {
  const j = i + paso;
  if (j < 0 || j >= f.componentes.length) return f;
  const c = [...f.componentes];
  [c[i], c[j]] = [c[j]!, c[i]!];
  return { ...f, componentes: c };
}

/** Quita un componente; el derivado que salía de él queda sin base (la validación lo marca). */
export function quitarComponente(f: FormularioCombo, clave: string): FormularioCombo {
  return { ...f, componentes: f.componentes.filter((k) => k.clave !== clave).map((k) => (k.base_clave === clave ? { ...k, base_clave: null } : k)) };
}

/** Los productos que ya están en el combo (como componente o como empaque), para no elegirlos dos veces. */
export function productosDelCombo(f: FormularioCombo): Set<string> {
  return new Set(f.componentes.flatMap((k) => [k.producto.producto_id, ...k.empaques.map((e) => e.producto.producto_id)]));
}

/**
 * Ya llegaron todas las presentaciones de un producto (las de un combo guardado traían solo la elegida): se ponen en cada
 * fila y empaque de ese producto, sin perder la elegida aunque hoy esté desactivada.
 */
export function completarProducto(f: FormularioCombo, p: ProductoResumenDTO): FormularioCombo {
  const nuevo = (actual: ProductoElegido): ProductoElegido => {
    if (actual.producto_id !== p.id) return actual;
    const activas = presentacionesActivas(p.presentaciones);
    const faltan = actual.presentaciones.filter((x) => !activas.some((a) => a.id === x.id));
    return { ...actual, activo: p.activo, fraccion_minima: p.fraccion_minima, presentaciones: [...faltan, ...activas], completo: true };
  };
  return { ...f, componentes: f.componentes.map((k) => ({ ...k, producto: nuevo(k.producto), empaques: k.empaques.map((e) => ({ ...e, producto: nuevo(e.producto) })) })) };
}

/** La unidad de la medida en singular, para «Consumo por m²» o «Consumo por metro lineal». */
const UNIDAD_SINGULAR: Record<UnidadMedidaCombo, string> = { m2: 'm²', ml: 'metro lineal', m3: 'm³', unidad: 'unidad' };

/** El rótulo del número del componente según cómo sale la cantidad. */
export function etiquetaFactor(modo: ModoComponente, tipo: TipoCombo, unidad: UnidadMedidaCombo | ''): string {
  const u = unidad || 'm2';
  if (modo === 'fijo') return tipo === 'fijo' ? 'Cantidad' : 'Cantidad por trabajo';
  if (modo === 'consumo') return `Consumo por ${UNIDAD_SINGULAR[u]}`;
  if (modo === 'rendimiento') return `Cubre (${UNIDAD_MEDIDA_ETIQUETA[u]}) cada uno`;
  return '';
}

/** Cómo se lee cada modo en la lista, con la unidad de la receta. */
export function etiquetaModo(modo: ModoComponente, unidad: UnidadMedidaCombo | ''): string {
  const u = unidad || 'm2';
  if (modo === 'fijo') return 'Cantidad fija';
  if (modo === 'consumo') return `Consumo por ${UNIDAD_SINGULAR[u]}`;
  if (modo === 'rendimiento') return 'Rendimiento (lo que cubre cada uno)';
  return 'Derivado de otro producto';
}

/** Modos que admite el tipo: el combo fijo no se calcula por medida. */
export const modosDelTipo = (tipo: TipoCombo): ModoComponente[] => (tipo === 'fijo' ? ['fijo', 'derivado'] : ['fijo', 'consumo', 'rendimiento', 'derivado']);
